import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Landmark, Plus, Trash2 } from 'lucide-react';
import { cn } from '@siliconx/shared/ui/utils';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { Field, TextInput } from '@siliconx/shared/ui/Field';
import type {
  BuyerCostConstantKind,
  BuyerCostConstantListResponse,
  BuyerCostConstantWriteRow,
} from '@siliconx/shared';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';

/**
 * SIL-1071 — org house rates / cost constants editor (design spec §4 "The
 * builder" costs panel + §1 "Cost kinds"). Backs `GET/PUT
 * /api/client/buyer-cost-constants` (apps/api/src/routes/client/
 * buyer-cost-constants.ts). Any org member may edit — no admin gate, same
 * posture as financial_scenario (681).
 *
 * Grouped by `kind`, not by key/label: the engine (a future
 * purchase-engine.ts, SIL-1072) keys strictly off `kind`, so grouping by it
 * here is what teaches the user which multiplier a constant feeds — "what
 * multiplies by month" vs "what multiplies by the bid" (spec §4).
 *
 * Whole-replace PUT — every row in the local edit buffer is sent, and any
 * standard key the user deletes from this screen is genuinely removed
 * (`costsConfigured` will read false again if that leaves every remaining
 * value at 0). Matches `buyer-cost-constants.ts`'s PUT semantics.
 */
export const Route = createFileRoute('/market-intelligence/cost-settings')({
  component: CostSettingsPage,
});

type EditableRow = BuyerCostConstantWriteRow;

const KIND_ORDER: BuyerCostConstantKind[] = [
  'per_unit',
  'per_unit_month',
  'pct_of_revenue',
  'pct_of_acquisition_cost',
  'fixed_per_analysis',
];

const KIND_LABELS: Record<BuyerCostConstantKind, string> = {
  per_unit: 'Per unit',
  per_unit_month: 'Per unit, per month held',
  pct_of_revenue: 'Percent of resale revenue',
  pct_of_acquisition_cost: 'Percent of acquisition cost (bid)',
  fixed_per_analysis: 'Fixed, once per analysis',
};

const KIND_HELP: Record<BuyerCostConstantKind, string> = {
  per_unit: 'Applied once per unit — freight, refurb parts, labor.',
  per_unit_month: 'Multiplied by the hold month — drives the sell-timing solve (storage).',
  pct_of_revenue: 'Fraction of resale revenue — marketplace fee, sales commission. Enter 0.15 for 15%.',
  pct_of_acquisition_cost: 'Fraction of the bid — import duty, tariff, insurance. Enter 0.15 for 15%.',
  fixed_per_analysis: 'Applied once for the whole bid, not per unit — truck, travel, auction fee.',
};

const PERCENT_KINDS = new Set<BuyerCostConstantKind>(['pct_of_revenue', 'pct_of_acquisition_cost']);

const LABOR_KEY = 'labor_usd_per_unit';

function toEditable(row: BuyerCostConstantListResponse['rows'][number]): EditableRow {
  return { key: row.key, label: row.label, kind: row.kind, value: row.value };
}

function Toast({
  message,
  type,
  onDismiss,
}: {
  message: string;
  type: 'success' | 'error';
  onDismiss: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div
      className={cn(
        'fixed bottom-6 right-6 z-50 rounded-lg px-4 py-3 text-sm font-medium shadow-lg',
        type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
      )}
    >
      {message}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Minutes x wage labor helper (spec §4: "a minutes-times-wage helper that
// writes labor_usd_per_unit"). Purely a UI convenience — it computes
// minutes/60 * hourlyWage and writes the result into the labor row's value;
// nothing about the helper's inputs is persisted server-side.
// ---------------------------------------------------------------------------
function LaborHelper({ onApply }: { onApply: (value: number) => void }) {
  const [minutes, setMinutes] = useState<number>(0);
  const [hourlyWage, setHourlyWage] = useState<number>(0);
  const computed = (minutes / 60) * hourlyWage;

  return (
    <div className="mt-2 flex flex-wrap items-end gap-3 rounded-md border border-dashed border-border bg-muted/50 p-3">
      <Field label="Minutes per unit">
        <TextInput
          type="number"
          min={0}
          step="1"
          value={minutes}
          onChange={(e) => setMinutes(Number(e.target.value))}
          className="w-28"
        />
      </Field>
      <Field label="Hourly wage (USD)">
        <TextInput
          type="number"
          min={0}
          step="0.01"
          value={hourlyWage}
          onChange={(e) => setHourlyWage(Number(e.target.value))}
          className="w-28"
        />
      </Field>
      <div className="pb-2 text-sm text-muted-foreground">
        = <span className="font-medium text-foreground">${computed.toFixed(2)}</span> / unit
      </div>
      <Button type="button" onClick={() => onApply(Number(computed.toFixed(4)))}>
        Apply to labor rate
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One editable cost-constant row.
// ---------------------------------------------------------------------------
function CostRowEditor({
  row,
  onChange,
  onRemove,
  removable,
}: {
  row: EditableRow;
  onChange: (next: EditableRow) => void;
  onRemove: () => void;
  removable: boolean;
}) {
  const isPercent = PERCENT_KINDS.has(row.kind);
  return (
    <div className="flex flex-wrap items-end gap-3 border-b border-border py-3 last:border-b-0">
      <Field label="Label" className="min-w-[10rem] flex-1">
        <TextInput
          value={row.label}
          onChange={(e) => onChange({ ...row, label: e.target.value })}
        />
      </Field>
      <Field label={isPercent ? 'Value (fraction, e.g. 0.15 = 15%)' : 'Value (USD)'} className="w-40">
        <TextInput
          type="number"
          min={0}
          max={isPercent ? 1 : undefined}
          step="0.0001"
          value={row.value}
          onChange={(e) => onChange({ ...row, value: Number(e.target.value) })}
        />
      </Field>
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${row.label}`}
          className="mb-1 rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-red-600"
        >
          <Trash2 className="size-4" />
        </button>
      )}
      {row.key === LABOR_KEY && <LaborHelper onApply={(value) => onChange({ ...row, value })} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-custom-constant form.
// ---------------------------------------------------------------------------
function AddConstantForm({ onAdd, existingKeys }: { onAdd: (row: EditableRow) => void; existingKeys: Set<string> }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<BuyerCostConstantKind>('per_unit');
  const [error, setError] = useState<string | null>(null);

  function slugify(text: string): string {
    return text
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  function submit() {
    const key = slugify(label);
    if (!key) {
      setError('Enter a label to derive a key from.');
      return;
    }
    if (existingKeys.has(key)) {
      setError(`A cost constant with key "${key}" already exists.`);
      return;
    }
    onAdd({ key, label: label.trim(), kind, value: 0 });
    setLabel('');
    setKind('per_unit');
    setError(null);
    setOpen(false);
  }

  if (!open) {
    return (
      <Button type="button" onClick={() => setOpen(true)} className="mt-4">
        <Plus className="mr-1 size-4" /> Add custom constant
      </Button>
    );
  }

  return (
    <div className="mt-4 space-y-3 rounded-md border border-border p-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Label" className="min-w-[12rem] flex-1">
          <TextInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Refrigerated storage" />
        </Field>
        <Field label="Kind" className="w-64">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as BuyerCostConstantKind)}
            className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            {KIND_ORDER.map((k) => (
              <option key={k} value={k}>{KIND_LABELS[k]}</option>
            ))}
          </select>
        </Field>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <Button type="button" onClick={submit}>Add</Button>
        <button
          type="button"
          onClick={() => { setOpen(false); setError(null); }}
          className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
function CostSettingsPage() {
  const queryClient = useQueryClient();

  const dataQ = useQuery<BuyerCostConstantListResponse>({
    queryKey: qk.marketIntel.costConstants(),
    queryFn: async () => {
      const res = await api.api.client['buyer-cost-constants'].$get();
      await throwIfNotOk(res);
      return res.json();
    },
  });

  const [rows, setRows] = useState<EditableRow[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (dataQ.data) setRows(dataQ.data.rows.map(toEditable));
  }, [dataQ.data]);

  const grouped = useMemo(() => {
    const byKind = new Map<BuyerCostConstantKind, EditableRow[]>();
    for (const kind of KIND_ORDER) byKind.set(kind, []);
    for (const row of rows ?? []) {
      if (!byKind.has(row.kind)) byKind.set(row.kind, []);
      byKind.get(row.kind)!.push(row);
    }
    return byKind;
  }, [rows]);

  function updateRow(key: string, next: EditableRow) {
    setRows((prev) => (prev ? prev.map((r) => (r.key === key ? next : r)) : prev));
  }

  function removeRow(key: string) {
    setRows((prev) => (prev ? prev.filter((r) => r.key !== key) : prev));
  }

  async function save() {
    if (!rows) return;
    setSaving(true);
    try {
      const res = await api.api.client['buyer-cost-constants'].$put({ json: { rows } });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      setToast({ message: 'Cost settings saved.', type: 'success' });
      void queryClient.invalidateQueries({ queryKey: qk.marketIntel.costConstants() });
    } catch (ex: unknown) {
      setToast({ message: (ex as Error).message, type: 'error' });
    } finally {
      setSaving(false);
    }
  }

  const existingKeys = new Set((rows ?? []).map((r) => r.key));

  return (
    <div className="space-y-6">
      <div>
        <PageHeader title="Cost Settings" pinTo="/market-intelligence/cost-settings" />
        <p className="mt-1 text-sm text-muted-foreground">
          Your organization's house rates for storage, freight, labor, refurb parts, marketplace
          fees and import duty. These feed the Market Intelligence margin engine — percent kinds
          are fractions (0.15 = 15%).
        </p>
      </div>

      {dataQ.data && !dataQ.data.costsConfigured && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <Landmark className="mt-0.5 size-4 shrink-0" />
          <span>
            No cost constant has a non-zero value yet. Purchase analyses will show margin as
            revenue minus bid with no cost basis until you set at least one rate below.
          </span>
        </div>
      )}

      <QueryBoundary query={dataQ} errorTitle="Couldn't load cost settings.">
        {() =>
          rows && (
            <>
              {KIND_ORDER.map((kind) => {
                const kindRows = grouped.get(kind) ?? [];
                if (kindRows.length === 0) return null;
                return (
                  <Card key={kind} as="section" className="space-y-1">
                    <h2 className="text-sm font-semibold text-foreground">{KIND_LABELS[kind]}</h2>
                    <p className="mb-2 text-xs text-muted-foreground">{KIND_HELP[kind]}</p>
                    {kindRows.map((row) => (
                      <CostRowEditor
                        key={row.key}
                        row={row}
                        onChange={(next) => updateRow(row.key, next)}
                        onRemove={() => removeRow(row.key)}
                        removable
                      />
                    ))}
                  </Card>
                );
              })}

              <AddConstantForm
                existingKeys={existingKeys}
                onAdd={(row) => setRows((prev) => (prev ? [...prev, row] : [row]))}
              />

              <div className="flex items-center gap-2 pt-2">
                <Button type="button" disabled={saving} onClick={save}>
                  {saving ? 'Saving…' : 'Save cost settings'}
                </Button>
              </div>
            </>
          )
        }
      </QueryBoundary>

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </div>
  );
}
